import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { normalizeTimestamp } from "../../utils/dateUtils";
import "./Heatmap.css";

function HourlyActiveHeatmap() {
  const [hours, setHours] = useState(Array(24).fill(0));
  const [loading, setLoading] = useState(true);

  // janela de 24h (ms)
  const MS_24H = 24 * 60 * 60 * 1000;

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "users"),
      (snap) => {
        const hourCounts = Array(24).fill(0);
        const now = Date.now();

        snap.forEach((doc) => {
          const data = doc.data();

          // ✅ Campo principal
          let lastActive = normalizeTimestamp(data.lastActiveAt);

          // ✅ Fallback (caso algum user antigo use outro campo)
          if (!lastActive) lastActive = normalizeTimestamp(data.lastActive);

          if (!lastActive) return;

          const diffMs = now - lastActive.getTime();

          // ✅ Apenas últimos 24h
          if (diffMs <= MS_24H) {
            const hour = lastActive.getHours(); // hora local do navegador
            if (hour >= 0 && hour <= 23) hourCounts[hour]++;
          }
        });

        setHours(hourCounts);
        setLoading(false);
      },
      (err) => {
        console.error("Erro ao carregar atividade por hora:", err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const maxCount = useMemo(() => {
    const m = Math.max(...hours);
    return m <= 0 ? 1 : m;
  }, [hours]);

  const total24h = useMemo(() => {
    return hours.reduce((sum, v) => sum + v, 0);
  }, [hours]);

  if (loading) {
    return (
      <div className="chartBox">
        <h3>Atividade por hora (24h) 🔥</h3>
        <div className="chart-loading">Carregando gráfico...</div>
      </div>
    );
  }

  return (
    <div className="chartBox">
      <h3>Atividade por hora (24h) 🔥</h3>

      <div className="heatmap">
        {hours.map((count, h) => {
          // intensidade baseada no maior valor do dia
          // mínimo pra não ficar “invisível” quando count > 0
          const intensity =
            count <= 0 ? 0.06 : Math.min(0.95, 0.18 + (count / maxCount) * 0.77);

          return (
            <div
              key={h}
              className={`heatmap-cell ${count > 0 ? "is-active" : ""}`}
              style={{
                backgroundColor: `rgba(255, 79, 154, ${intensity})`,
              }}
              title={`${h}h — ${count} ativo(s) nas últimas 24h`}
            >
              <span className="heatmap-hour">{h}h</span>
              <small className="heatmap-count">{count}</small>
            </div>
          );
        })}
      </div>

      {/* ✅ Rodapé com explicação */}
      <div className="heatmap-footer">
        <div className="heatmap-summary">
          Total (24h): <strong>{total24h}</strong> atividade(s) • Pico:{" "}
          <strong>{maxCount}</strong> em 1h
        </div>

        <p className="heatmap-desc">
          <strong>Como ler:</strong> cada quadrado representa a <strong>hora do dia</strong>{" "}
          em que o usuário ficou ativo (com base em <code>lastActiveAt</code>), considerando{" "}
          apenas as <strong>últimas 24 horas</strong>. Quanto mais “forte” o rosa,{" "}
          mais usuários ficaram ativos naquela hora.
        </p>
      </div>
    </div>
  );
}

export default HourlyActiveHeatmap;
